import { Extension } from '@tiptap/react';
import { Plugin } from '@tiptap/pm/state';
import { Decoration, DecorationSet } from '@tiptap/pm/view';
import * as Y from 'yjs';
import { ySyncPlugin, yUndoPlugin, ySyncPluginKey, yUndoPluginKey, undo, redo, absolutePositionToRelativePosition, relativePositionToAbsolutePosition } from '@tiptap/y-tiptap';
import { CRDT_FIELD, decodeDocumentBytes, encodeDocumentBytes } from '@cdf/api-zod';
import { presenceToneForId, renderPresenceCursor } from './document-presence';

export type LivePeer = { sessionId: string; actorId: string; name?: string | null; canEdit?: boolean; cursor?: {anchor:string;head:string} };
export type LiveEditorConnection = { document: Y.Doc; peers: LivePeer[]; cursor: (value?: {anchor:string;head:string}) => void };
/** One binding owner. Native transport supplies authenticated identities, never client awareness names. */
export const DocumentLiveEditing = Extension.create<{ connection: LiveEditorConnection }>({
  name: 'documentLiveEditing',
  addCommands() {
    return { undo: () => ({state,tr,dispatch}) => { tr.setMeta('preventDispatch',true); return dispatch ? undo(state) : Boolean(yUndoPluginKey.getState(state)?.undoManager.undoStack.length); },
      redo: () => ({state,tr,dispatch}) => { tr.setMeta('preventDispatch',true); return dispatch ? redo(state) : Boolean(yUndoPluginKey.getState(state)?.undoManager.redoStack.length); } };
  },
  addKeyboardShortcuts() {
    return { 'Mod-z': () => undo(this.editor.state), 'Mod-Shift-z': () => redo(this.editor.state), 'Mod-y': () => redo(this.editor.state) };
  },
  addProseMirrorPlugins() {
    const connection = this.options.connection;
    const fragment = connection.document.getXmlFragment(CRDT_FIELD);
    return [
      ySyncPlugin(fragment), yUndoPlugin(),
      new Plugin({
        props: {
          decorations(state) {
            const binding = ySyncPluginKey.getState(state)?.binding;
            if (!binding) return DecorationSet.empty;
            const result: Decoration[] = [];
            for (const peer of connection.peers) {
              // Viewers follow along read-only; only editors' carets are drawn, as in Solution Studio.
              if (!peer.cursor || peer.canEdit === false) continue;
              try {
                const resolve = (value: string) => relativePositionToAbsolutePosition(connection.document, fragment, Y.decodeRelativePosition(decodeDocumentBytes(value,1536)), binding.mapping);
                const anchor = resolve(peer.cursor.anchor), head = resolve(peer.cursor.head);
                if (anchor === null || head === null || anchor < 0 || head < 0 || anchor > state.doc.content.size || head > state.doc.content.size) continue;
                if (anchor !== head) result.push(Decoration.inline(Math.min(anchor,head),Math.max(anchor,head),{class:presenceToneForId(peer.actorId).selection,'data-peer-selection':peer.sessionId}));
                result.push(Decoration.widget(head, () => renderPresenceCursor(peer),{key:`${peer.sessionId}:${peer.name ?? ''}`,side:1}));
              } catch { /* Deleted/unresolvable relative positions are not guessed. */ }
            }
            return DecorationSet.create(state.doc,result);
          }
        },
        view: view => {
          let last='',disposed=false,queued=false;
          const redraw=(transaction: Y.Transaction)=>{if(transaction.changed.size===0||queued||disposed)return;queued=true;queueMicrotask(()=>{queued=false;if(disposed)return;view.dispatch(view.state.tr.setMeta('addToHistory',false));publish();});};
          connection.document.on('afterTransaction',redraw);
          const publish=() => {
            if (!view.hasFocus()) { if(last){last='';connection.cursor();} return; }
            const binding=ySyncPluginKey.getState(view.state)?.binding;
            if(!binding)return;
            const {anchor,head}=view.state.selection;
            const value={anchor:encodeDocumentBytes(Y.encodeRelativePosition(absolutePositionToRelativePosition(anchor,fragment,binding.mapping))),
              head:encodeDocumentBytes(Y.encodeRelativePosition(absolutePositionToRelativePosition(head,fragment,binding.mapping)))};
            const key=JSON.stringify(value);if(key!==last){last=key;connection.cursor(value);}
          };
          view.dom.addEventListener('blur',publish);view.dom.addEventListener('focus',publish);
          return {update:publish,destroy(){disposed=true;connection.document.off('afterTransaction',redraw);connection.cursor();view.dom.removeEventListener('blur',publish);view.dom.removeEventListener('focus',publish);}};
        }
      })
    ];
  }
});
export const isLiveDocumentTransaction = (transaction: {getMeta:(key: typeof ySyncPluginKey)=>unknown}) => Boolean(transaction.getMeta(ySyncPluginKey));
